import { Button, Center, Portal, Text } from '@chakra-ui/react';
import Loading from '@components/Loading';
import { useCheckoutConfigContext } from '@contexts/CheckoutConfig';
import { useDonateButtonRef } from '@contexts/DonateButtonRef';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { FormValues, PAYMENT_METHOD_TYPE, StripePM, STRIPE_PAYMENT_TYPE } from '@types';
import { FC, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { When } from 'react-if';
import * as Sentry from '@sentry/react';
import useStripeOptions from '../useStripeOptions';
import composeErrorMessage from '@utils/composeErrorMessage';

interface Props {
  formIsLoading: boolean;
  onComplete: () => Promise<void>;
  onError: (error: string) => void;
}

const CreditCardPanel: FC<Props> = ({ formIsLoading, onComplete, onError }) => {
  const { checkout } = useCheckoutConfigContext();

  const stripeMethod = useMemo<StripePM | undefined>(() => {
    return checkout.enabled_payment_methods.find(
      (method) => method.type === PAYMENT_METHOD_TYPE.STRIPE,
    ) as StripePM | undefined;
  }, [checkout]);

  const { stripeOptions, loading, error } = useStripeOptions([STRIPE_PAYMENT_TYPE.CREDIT_CARD]);

  if (!stripeMethod!.stripe_account_id || !stripeMethod!.stripe_pub_key)
    throw new Error('You need to provide the Stripe keys to use Credit Card method');

  const stripePromise = useMemo(
    () =>
      loadStripe(stripeMethod!.stripe_pub_key, {
        stripeAccount: stripeMethod!.stripe_account_id,
      }),
    [],
  );

  if (loading || !stripePromise || !stripeOptions) return <Loading h="200px" />;
  if (error)
    return (
      <Center>
        <Text>{error}</Text>
      </Center>
    );

  return (
    <div>
      <Elements stripe={stripePromise} options={stripeOptions}>
        <CreditCard formIsLoading={formIsLoading} onComplete={onComplete} onError={onError} />
      </Elements>
    </div>
  );
};

const CreditCard: FC<Props> = ({ formIsLoading, onComplete, onError }) => {
  const { ref } = useDonateButtonRef();
  const { watch } = useFormContext<FormValues>();
  const [loading, setLoading] = useState(false);
  const { checkout } = useCheckoutConfigContext();
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();

  const paymentMethodId = watch('paymentMethodId');

  const selectedMethod = useMemo(() => {
    if (paymentMethodId && checkout) {
      const method = checkout.enabled_payment_methods.find(
        (method) => method.id === paymentMethodId,
      );
      return method;
    }

    return null;
  }, [paymentMethodId, checkout]);

  const handleDonate = async () => {
    setLoading(true);

    if (!stripe || !elements) {
      Sentry.captureMessage(
        composeErrorMessage({
          code: 'STRIPE_NOT_LOADED',
          checkout,
          campaignId: checkout.campaign.id,
          organization: checkout.organization.name,
          token: checkout.token,
        }),
      );
      throw new Error('Internal - Stripe not loaded');
    }

    const { error } = await stripe!.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (error) {
      onError(error.message || t("We couldn't process your payment, please try again later"));
      Sentry.captureMessage(
        composeErrorMessage({
          code: 'CREDIT_CARD_ERROR',
          errorObject: error,
          checkout,
          campaignId: checkout.campaign.id,
          organization: checkout.organization.name,
          token: checkout.token,
        }),
      );
    } else {
      try {
        await onComplete();
      } catch (err) {
        console.error(err);
        onError(t("We couldn't process your payment, please try again later"));
        Sentry.captureMessage(
          composeErrorMessage({
            code: 'CREDIT_CARD_COMPLETE_ERROR',
            errorObject: error,
            checkout,
            campaignId: checkout.campaign.id,
            organization: checkout.organization.name,
            token: checkout.token,
          }),
        );
      }
    }

    setLoading(false);
  };

  if (!ref?.current) return null;

  return (
    <div>
      <PaymentElement />

      <Portal containerRef={ref}>
        <When
          condition={
            selectedMethod?.type === PAYMENT_METHOD_TYPE.STRIPE &&
            selectedMethod?.stripe_payment_type == STRIPE_PAYMENT_TYPE.CREDIT_CARD
          }
        >
          <Button
            size="lg"
            w={{ base: 'full', lg: '100px' }}
            alignSelf="flex-end"
            mt="auto"
            colorScheme="primary"
            isLoading={loading || formIsLoading}
            disabled={loading || formIsLoading}
            onClick={handleDonate}
          >
            {t('Donate')}
          </Button>
        </When>
      </Portal>
    </div>
  );
};

export default CreditCardPanel;
